import React from 'react';
import './src/css/style.css';

import { initializeAdjustSdk, reinitializeAdjustSdk, shouldReloadForAdjustAppChange } from './src/utils/adjustSdk';
import AppDownloadModalProvider from './src/components/app-download-modal-provider';
import { isTagFreePath } from './src/utils/notice-route';

export const wrapRootElement = ({ element }) => (
  <AppDownloadModalProvider>{element}</AppDownloadModalProvider>
);

export function onClientEntry() {
  // GEIGHT-7: no third-party SDK on the move page or the notice page (src/utils/notice-route.js).
  if (isTagFreePath(window.location.pathname)) {
    return;
  }
  initializeAdjustSdk();
}

export function shouldUpdateScroll({ routerProps: { location } }) {
  // Hash targets scroll to an element rather than to the top, and the glide is wanted there
  // (e.g. the same-page <Link to="/id/karir/#openings">), so leave those alone.
  if (location.hash) {
    return true;
  }

  // style.base.css sets `scroll-behavior: smooth` on the root element, which also applies to
  // the scroll reset Gatsby performs on every client-side navigation.
  //
  // When the destination page is shorter than the current scroll offset, the browser first
  // clamps the retained offset to the new page's maximum — its bottom — and only then does
  // the smooth reset glide up over ~600ms. So the reader lands at the FOOT of the new page
  // and watches it scroll. Measured on the live site going from an article to an author
  // profile via "Baca lebih lanjut": offset 6631 on a 9552px page, clamped to 2214 on the
  // 2925px author page, then animated 2214 -> 1739 -> 517 -> 164 -> 32 -> 0. If anything
  // interrupts that animation the reader is simply left at the bottom.
  //
  // Make the reset instant so it is atomic and never painted. Clearing the override on the
  // next tick is safe only because gatsby-react-router-scroll calls window.scrollTo
  // synchronously right after this predicate returns (scroll-handler.js, windowScroll) — if
  // that scroll ever moves behind a rAF or timer, this override expires too early and the
  // bug returns.
  document.documentElement.style.scrollBehavior = 'auto';
  window.setTimeout(() => {
    document.documentElement.style.scrollBehavior = '';
  }, 0);
  return true;
}

// Replaces gatsby-plugin-google-tagmanager's browser half (the SSR half is the deferred
// loader in gatsby-ssr.js): same 'gatsby-route-change' event, same 50ms title-settle delay.
function pushRouteChangeToGTM() {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  setTimeout(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'gatsby-route-change' });
  }, 50);
}

// The plugin's enableWebVitalsTracking, verbatim event shape: pushes 'core-web-vitals'
// events (LCP/FID/CLS) into the dataLayer, which the deferred GTM flushes once loaded.
const webVitalsSent = new Set();
function sendWebVitalsToGTM() {
  const send = (data) => {
    if (webVitalsSent.has(data.name)) {
      return;
    }
    webVitalsSent.add(data.name);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'core-web-vitals',
      webVitalsMeasurement: {
        name: data.name,
        id: data.id,
        value: Math.round(data.name === 'CLS' ? data.value * 1000 : data.value)
      }
    });
  };
  const debounce = (fn, timeout) => {
    let timer = null;
    return (...args) => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(fn, timeout, ...args);
    };
  };
  return import('web-vitals/base').then(({ getLCP, getFID, getCLS }) => {
    getCLS(debounce(send, 3000), true);
    getFID(send, true);
    getLCP(debounce(send, 3000), true);
  });
}

export function onInitialClientRender() {
  if (process.env.NODE_ENV === 'production' && !isTagFreePath(window.location.pathname)) {
    sendWebVitalsToGTM();
  }
}

export function onRouteUpdate({ location, prevLocation }) {
  if (!location || isTagFreePath(location.pathname)) {
    return;
  }

  pushRouteChangeToGTM();

  if (prevLocation && shouldReloadForAdjustAppChange(prevLocation.pathname, location.pathname)) {
    // Skip hard reload when user manually switched locale — a reload would trigger
    // CloudFront's /id redirect and send them back before reaching the target page.
    // Instead, stop and reinitialize the Adjust SDK for the new app kind.
    const isManualLocaleSwitch = sessionStorage.getItem('preferred_locale') !== null;
    if (isManualLocaleSwitch) {
      reinitializeAdjustSdk(location.pathname);
      return;
    }

    window.location.replace(`${location.pathname}${location.search}${location.hash}`);
    return;
  }

  initializeAdjustSdk(location.pathname);
}
